
import { Component, Prop, Watch, Mixins } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { MatrixCamera } from "@/store/site-monitor-cameras/types";
import MatrixCameraMixin from "@/components/media/sidebar/MatrixCameraMixin";

const SMCameras = namespace("siteMonitorCameras");

@Component
export default class AreaCameras extends Mixins(MatrixCameraMixin) {
	@SMCameras.Getter("getAreaCameras") private groupCameras: MatrixCamera[];
	@SMCameras.Action private fetchAreaCameras: (groupId: number) => Promise<void>;

	@SMCameras.Mutation private setAreaCameras: (cameras: MatrixCamera[]) => void;
	@SMCameras.Mutation private setAreaCameraIndex: ({ cameraId, index }) => void;

	@Prop(String) private filter: string = "";

	private open: boolean = false;

	/**
	 * Gets the site title for the current event.
	 */
	private get siteTitle() {
		return this.eventDetails?.siteTitle ?? "";
	}

	/**
	 * Returns the cameras for the current area, filtered by the users query.
	 */
	private get areaCameras() {
		if (this.filter === "") return this.groupCameras;

		const filterValue = this.filter.toLocaleLowerCase();
		return this.groupCameras.filter(camera => camera.title.toLocaleLowerCase().indexOf(filterValue) > -1);
	}

	/**
	 * Handler for when the event is changed. Fetches the cameras for the new event's area.
	 */
	@Watch("eventId")
	private async onEventChanged(eventId: number, oldEventId: number) {
		if (this.eventDetails) {
			this.getGroupCameras(this.eventDetails.groupID);
		} else {
			this.setAreaCameras([]);
		}
	}

	/**
	 * When the matrix is updated, updates the area camera indices.
	 */
	@Watch("mediaMatrixLastChanged")
	private onMatrixChanged() {
		if (this.groupCameras && this.groupCameras.length > 0) {
			this.setGroupCameraIndexes();
		}
	}

	/**
	 * When the component is being torn down, clears current area cameras.
	 */
	private beforeDestroy() {
		this.setAreaCameras([]);
	}

	/**
	 * Updates the area cameras to ensure their index matches the location of the camera in the matrix.
	 */
	private setGroupCameraIndexes() {
		const deviceIndexes = {};
		const maxtrixContents = this.allMatrixContents;

		for (var matrixIndex in maxtrixContents) {
			if (maxtrixContents[matrixIndex] != null && maxtrixContents[matrixIndex].camera != null) {
				deviceIndexes[maxtrixContents[matrixIndex].camera.objectID] = parseInt(matrixIndex);
			}
		}

		this.groupCameras.forEach(camera => {
			this.setAreaCameraIndex({
				cameraId: camera.objectID,
				index: deviceIndexes[camera.objectID] != null ? deviceIndexes[camera.objectID] : null
			});
		});
	}

	/**
	 * Fetches the cameras for a given group id, and updates their indices.
	 */
	private async getGroupCameras(groupID: number) {
		await this.$nextTick();
		await this.fetchAreaCameras(groupID);
		this.setGroupCameraIndexes();
	}
}
